import { Box } from "@mui/material";
import { useRef, useState } from "react";
import ReactPlayer from "react-player";
import { useBoolean } from "usehooks-ts";

import {
  CommonGenerationTileProps,
  GenerationTileAction,
} from "@/components/Tiles/GenerationTile/types";
import {
  CircularProgress,
  TileBase,
  TileLikeButton,
  TileObjectTypeIndicator,
} from "@/components/ui-v2";
import { SessionFileFragment } from "@/features/forge/forge-session/queries.graphql";
import { useOnValueChanged } from "@/hooks";
import { getHasAudio } from "@/utils/video";

import { VideoGenerationTileButtons } from "./VideoGenerationTileButtons";
import { VideoGenerationTileControls } from "./VideoGenerationTileControls";

export interface VideoGenerationTileProps
  extends CommonGenerationTileProps<SessionFileFragment> {
  isPlaying?: boolean;
  isLazyLoaded?: boolean;
}

type VideoDisplayState = "loading" | "preview" | "active";

export const VideoGenerationTile = (props: VideoGenerationTileProps) => {
  const {
    object,
    isSelected = false,
    isSelectable = false,
    isLiking = false,
    isPlaying = false,
    isLazyLoaded = false,
    onAction,
    ...rest
  } = props;

  const { isLiked, previewUrl, url } = object;
  const playerRef = useRef<ReactPlayer>(null);
  const [videoDisplayState, setVideoDisplayState] = useState<VideoDisplayState>(
    isLazyLoaded ? "preview" : "active",
  );
  const [hasAudio, setHasAudio] = useState(false);
  const { value: isMuted, toggle: toggleMuted } = useBoolean(true);

  const handleClickPreview = (e: React.MouseEvent) => {
    e.stopPropagation();
    setVideoDisplayState("loading");
    onAction?.(e, "TOGGLE_PLAY");
    onAction?.(e, "SELECT");
  };

  const handleReady = () => {
    const player = playerRef.current?.getInternalPlayer() as HTMLVideoElement;

    if (player) {
      setVideoDisplayState("active");
      setHasAudio(getHasAudio(player));
    }
  };

  const handleIsPlayingChanged = () => {
    const player = playerRef.current?.getInternalPlayer() as HTMLVideoElement;
    // Happens if video was in preview and playing is toggled by
    // something else than clicking the play button (ie: button outside of the tile).
    if (videoDisplayState === "preview") {
      setVideoDisplayState("loading");
    }

    if (!isPlaying && player && !isSelected) {
      player.currentTime = 0;
    }
  };

  const handleControlsAction = (
    e: React.MouseEvent,
    action: GenerationTileAction,
  ) => {
    if (action === "TOGGLE_MUTE") {
      e.stopPropagation();
      toggleMuted();
    } else {
      onAction?.(e, action);
    }
  };

  const handleTileBaseAction =
    (action: GenerationTileAction) => (e: React.MouseEvent) => {
      onAction?.(e, action);
    };

  useOnValueChanged(isPlaying, handleIsPlayingChanged);

  return (
    <TileBase
      isSelected={isSelected}
      isSelectable={isSelectable}
      slots={{
        bottomLeftEl: VideoGenerationTileButtons,
        topLeftEl: TileObjectTypeIndicator,
        bottomRightEl: TileLikeButton,
        topRightEl: VideoGenerationTileControls,
      }}
      slotProps={{
        bottomLeftEl: {
          onAction,
        },
        topLeftEl: {
          isSelected,
          objectType: "video",
        },
        bottomRightEl: {
          isLiked,
          isLoading: isLiking,
          onAction,
          sx: {
            m: 1,
          },
        },
        topRightEl: {
          isPlaying,
          isMuted,
          onAction: handleControlsAction,
          disabledMuteButton: !hasAudio,
        },
      }}
      onClick={handleTileBaseAction("SELECT")}
      onDoubleClick={handleTileBaseAction("DOUBLE_CLICK")}
      onContextMenu={handleTileBaseAction("OPEN_CONTEXT_MENU")}
      {...rest}
    >
      {videoDisplayState !== "active" && (
        <>
          {previewUrl && (
            <Box role="button" onClick={handleClickPreview}>
              <img src={previewUrl} alt={`${object.name} preview`} />
            </Box>
          )}
          {videoDisplayState === "loading" && (
            <Box
              sx={{
                position: "absolute",
                top: "50%",
                left: "50%",
                transform: "translate(-50%, -50%)",
              }}
            >
              <CircularProgress color="secondary" />
            </Box>
          )}
        </>
      )}
      {/* ReactPlayer for the active video */}
      {["active", "loading"].includes(videoDisplayState) && (
        <ReactPlayer
          loop
          width="100%"
          height="100%"
          url={url}
          muted={isMuted}
          ref={playerRef}
          playing={isPlaying}
          onReady={handleReady}
          controls={false}
          style={{
            display: videoDisplayState === "loading" ? "none" : "block",
          }}
        />
      )}
    </TileBase>
  );
};
